import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import Text from 'design-system/Text';
import Modal from 'utils/packages/Modal';
import { rem } from 'design-system/values';
import EmojiText from 'design-system/EmojiText';
import { IconBoldArrow } from 'design-system/icons';
import IconCircle from 'design-system/icons/IconCircle';
import { Button, CardView, Spacer } from 'design-system';
import LogoBankAccount from 'design-system/LogoBankAccount';
import IconEmmaWallet from 'design-system/icons/IconEmmaWallet';
import { formatAmount } from 'utils';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { selectDefaultCurrency } from 'reducers/selectors';
import { AccountTypeDisplay } from '../../invest/constants';
const ConfirmTransferModalContent = ({ to, from, amount, onConfirm, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const defaultCurrency = useAppSelector(selectDefaultCurrency);
    const handleConfirm = useCallback(() => {
        Modal.hide(onConfirm);
    }, [onConfirm]);
    const fromInfo = useMemo(() => {
        if ('bankConnectionId' in from) {
            return {
                name: from.name,
                Icon: <LogoBankAccount size="R44" data={from}/>,
            };
        }
        if ('emoji' in from) {
            return {
                name: from.name,
                Icon: (<IconCircle width={44} height={44} color={colors.cards.onDark}>
            <EmojiText>{from.emoji}</EmojiText>
          </IconCircle>),
            };
        }
        if ('itemType' in from && from.itemType === 'investConnectedAccount') {
            return {
                name: from.bankName || 'Linked bank',
                Icon: <LogoBankAccount data={from} size="R44"/>,
            };
        }
        return {
            name: AccountTypeDisplay[from.accountType],
            Icon: <IconEmmaWallet size={44}/>,
        };
    }, [colors.cards.onDark, from]);
    const toInfo = useMemo(() => {
        if ('bankConnectionId' in to) {
            return {
                name: to.name,
                Icon: <LogoBankAccount size="R44" data={to}/>,
            };
        }
        if ('emoji' in to) {
            return {
                name: to.name,
                Icon: (<IconCircle width={44} height={44} color={colors.cards.onDark}>
            <EmojiText>{to.emoji}</EmojiText>
          </IconCircle>),
            };
        }
        if (to.itemType === 'investConnectedAccount') {
            return {
                name: to.bankName || 'Linked bank',
                Icon: <LogoBankAccount data={to} size="R44"/>,
            };
        }
        return {
            name: AccountTypeDisplay[to.accountType],
            Icon: <IconEmmaWallet size={rem(44)}/>,
        };
    }, [colors.cards.onDark, to]);
    return (<View>
      <Spacer h={8}/>
      <CardView style={styles.cardView}>
        <View style={styles.headerContainer}>
          <Text Numbers-24 centered>
            {formatAmount(amount, defaultCurrency, 2)}
          </Text>
          <Text TextThin-14 Secondary>
            Amount
          </Text>
        </View>
        <View style={styles.fromToContainer}>
          <View style={styles.fromTo}>
            {fromInfo.Icon}
            <Spacer h={8}/>
            <Text Text-14 numberOfLines={1}>
              {fromInfo.name}
            </Text>
          </View>

          <IconBoldArrow alt direction="right" colorKey="secondary"/>

          <View style={styles.fromTo}>
            {toInfo.Icon}
            <Spacer h={8}/>
            <Text Text-14 numberOfLines={1}>
              {toInfo.name}
            </Text>
          </View>
        </View>
      </CardView>
      <View style={staticStyles.btnContainer}>
        <Button brand onPress={handleConfirm} title="Confirm"/>
      </View>
    </View>);
};
export const ConfirmTransferModal = {
    show: (from, to, amount, action, onConfirm) => {
        Modal.show(<ConfirmTransferModalContent to={to} from={from} amount={amount} onConfirm={onConfirm}/>, {
            containerStyle: staticStyles.modal,
            title: `Confirm your ${action}`,
        });
    },
    hide: () => {
        Modal.hide();
    },
};
const staticStyles = StyleSheet.create({
    modal: {
        paddingHorizontal: 0,
        overflow: 'hidden',
    },
    btnContainer: {
        paddingTop: rem(16),
        paddingHorizontal: rem(16),
    },
});
const styleSet = createStyleSheets((colors) => ({
    cardView: {
        backgroundColor: colors.background.dark,
    },
    headerContainer: {
        paddingTop: rem(24),
        alignItems: 'center',
        paddingBottom: rem(8),
        paddingHorizontal: rem(16),
    },
    fromToContainer: {
        marginTop: rem(8),
        flexDirection: 'row',
        marginBottom: rem(16),
        alignItems: 'center',
    },
    fromTo: {
        flex: 1,
        alignItems: 'center',
        paddingVertical: rem(8),
        paddingHorizontal: rem(16),
    },
}));
